import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

import {
  CashEvent,
  Category,
  CategoryFormData,
  CategoryType,
  FreeAgentCategory,
  FreeAgentCategoryGroups,
  initialCategoryFormData,
} from '../types';

type CategoryRequestPayload = {
  categoryUrl: string;
  type: CategoryType;
  categoryName: string | null;
  startDateTime: string | null;
  endDateTime: string | null;
  interval: number | null;
  amount: number | null;
};

type SortDirection = 'asc' | 'desc';

type SortableValue = string | number | boolean | Date | null | undefined;

type SortConfig<K extends string> = {
  key: K;
  direction: SortDirection;
};

type CategorySortKey =
  | 'id'
  | 'categoryName'
  | 'type'
  | 'startDateTime'
  | 'endDateTime'
  | 'interval'
  | 'amount'
  | 'description';

type CashEventSortKey =
  | 'categoryId'
  | 'category'
  | 'categoryType'
  | 'occurrence'
  | 'amount'
  | 'description';

const categoryTypeLabels: Record<CategoryType, string> = {
  averaging: 'Averaging',
  regular: 'Regular',
  irregular: 'Irregular',
};

const formatCategoryType = (value?: CategoryType | string | null): string => {
  if (!value) {
    return '—';
  }

  if (value in categoryTypeLabels) {
    return categoryTypeLabels[value as CategoryType];
  }

  return String(value);
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; error?: string; detail?: string }
      | string
      | undefined;

    if (!data) {
      return fallback;
    }

    if (typeof data === 'string') {
      return data;
    }

    return data.message || data.error || data.detail || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const formatFreeAgentCategoryLabel = (category: FreeAgentCategory): string => {
  const parts: string[] = [];

  if (category.nominalCode) {
    parts.push(category.nominalCode);
  }

  if (category.description) {
    parts.push(category.description);
  }

  if (parts.length === 0 && category.url) {
    return category.url;
  }

  return parts.join(' — ') || 'Unknown category';
};

const normalizeDateString = (value: string): string => {
  if (value.includes('T')) {
    return value;
  }

  return value.replace(' ', 'T');
};

const toDateInputValue = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const normalized = normalizeDateString(value);
  const date = new Date(normalized);

  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().slice(0, 10);
  }

  const dateOnlyMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);

  return dateOnlyMatch ? dateOnlyMatch[1] : '';
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const normalized = normalizeDateString(value);
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const hasTimeComponent = /T\d{2}:\d{2}/.test(normalized);

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    ...(hasTimeComponent ? { timeStyle: 'short' } : {}),
  });
};

const toSortableDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizeDateString(value);
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const toApiDateTime = (date: string, endOfDay = false): string => {
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return date;
  }

  const [, yearStr, monthStr, dayStr] = match;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return date;
  }

  const dateObj = new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 999 : 0,
    ),
  );

  return dateObj.toISOString();
};

const formatAmount = (value?: number | string | null): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'number') {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'GBP',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  const numericValue = Number(value);

  if (!Number.isNaN(numericValue)) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'GBP',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  }

  return value;
};

const formatNumberInput = (value?: number | string | null): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

const SECONDS_IN_DAY = 24 * 60 * 60;

const formatIntervalInput = (value?: number | string | null): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const numericValue = typeof value === 'string' ? Number(value) : value;

  if (Number.isNaN(numericValue)) {
    return '';
  }

  const days = numericValue / SECONDS_IN_DAY;

  if (!Number.isFinite(days)) {
    return '';
  }

  const roundedDays = Number(days.toFixed(6));

  return roundedDays.toString();
};

const formatIntervalDisplay = (value?: number | string | null): string => {
  if (value === null || value === undefined) {
    return '—';
  }

  const numericValue = typeof value === 'string' ? Number(value) : value;

  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  const days = numericValue / SECONDS_IN_DAY;

  if (!Number.isFinite(days)) {
    return '—';
  }

  const precision = Math.abs(days) < 0.01 ? 6 : 2;
  const normalizedDays = Number(days.toFixed(precision));
  const label = normalizedDays === 1 ? 'day' : 'days';

  return `${normalizedDays} ${label}`;
};

const parseMaybeNumber = (value: number | string | null | undefined): number | string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return value;
  }

  const numericValue = Number(value);

  return Number.isNaN(numericValue) ? value : numericValue;
};

const compareValues = (a: SortableValue, b: SortableValue, direction: SortDirection): number => {
  const multiplier = direction === 'asc' ? 1 : -1;

  if (a === null || a === undefined) {
    if (b === null || b === undefined) {
      return 0;
    }

    return 1 * multiplier;
  }

  if (b === null || b === undefined) {
    return -1 * multiplier;
  }

  if (a instanceof Date && b instanceof Date) {
    return (a.getTime() - b.getTime()) * multiplier;
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * multiplier;
  }

  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return (Number(a) - Number(b)) * multiplier;
  }

  const stringA = String(a);
  const stringB = String(b);

  return stringA.localeCompare(stringB, undefined, {
    numeric: true,
    sensitivity: 'base',
  }) * multiplier;
};

const getAriaSort = (
  isActive: boolean,
  direction: SortDirection,
): 'ascending' | 'descending' | 'none' => {
  if (!isActive) {
    return 'none';
  }

  return direction === 'asc' ? 'ascending' : 'descending';
};

const getCashEventDate = (event: CashEvent): string | null => {
  const candidate =
    event.occursOn || event.occursAt || event.dueOn || event.startDateTime || event.endDateTime;

  return candidate ?? null;
};

const getCategorySortValue = (category: Category, key: CategorySortKey): SortableValue => {
  switch (key) {
    case 'id':
      return category.id;
    case 'categoryName':
      return category.categoryName ?? '';
    case 'type':
      return categoryTypeLabels[category.type] ?? category.type;
    case 'startDateTime':
      return toSortableDate(category.startDateTime);
    case 'endDateTime':
      return toSortableDate(category.endDateTime);
    case 'interval':
      return parseMaybeNumber(category.interval);
    case 'amount':
      return parseMaybeNumber(category.amount);
    case 'description':
      return category.freeagentCategory?.description ?? '';
    default:
      return '';
  }
};

const getCashEventSortValue = (event: CashEvent, key: CashEventSortKey): SortableValue => {
  switch (key) {
    case 'categoryId':
      if (event.categoryId !== null && event.categoryId !== undefined) {
        return event.categoryId;
      }

      if (event.id !== undefined && event.id !== null) {
        return event.id;
      }

      return null;
    case 'category':
      return event.categoryName ?? event.categoryUrl ?? '';
    case 'categoryType':
      return event.categoryType ?? '';
    case 'occurrence':
      return toSortableDate(getCashEventDate(event));
    case 'amount':
      return parseMaybeNumber(event.amount);
    case 'description':
      return event.description ?? event.notes ?? '';
    default:
      return '';
  }
};

const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({ ...initialCategoryFormData });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [viewingCategory, setViewingCategory] = useState<Category | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [freeAgentCategoryGroups, setFreeAgentCategoryGroups] = useState<FreeAgentCategoryGroups>({});
  const [freeAgentLoading, setFreeAgentLoading] = useState(false);
  const [freeAgentError, setFreeAgentError] = useState<string | null>(null);
  const [cashEvents, setCashEvents] = useState<CashEvent[]>([]);
  const [cashEventsLoading, setCashEventsLoading] = useState(false);
  const [cashEventsError, setCashEventsError] = useState<string | null>(null);
  const [categorySortConfig, setCategorySortConfig] = useState<SortConfig<CategorySortKey>>({
    key: 'id',
    direction: 'asc',
  });
  const [cashEventSortConfig, setCashEventSortConfig] = useState<SortConfig<CashEventSortKey>>({
    key: 'occurrence',
    direction: 'asc',
  });
  const [cashEventsStartDate, setCashEventsStartDate] = useState<string>(() => {
    const today = new Date();
    const pastMonth = new Date(today);
    pastMonth.setDate(today.getDate() - 30);
    return pastMonth.toISOString().slice(0, 10);
  });
  const [cashEventsEndDate, setCashEventsEndDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const sortedCategories = useMemo(() => {
    return categories
      .map((category, index) => ({ category, index }))
      .sort((a, b) => {
        const valueA = getCategorySortValue(a.category, categorySortConfig.key);
        const valueB = getCategorySortValue(b.category, categorySortConfig.key);
        const result = compareValues(valueA, valueB, categorySortConfig.direction);

        if (result !== 0) {
          return result;
        }

        return a.index - b.index;
      })
      .map(({ category }) => category);
  }, [categories, categorySortConfig]);

  const sortedCashEvents = useMemo(() => {
    return cashEvents
      .map((event, index) => ({ event, index }))
      .sort((a, b) => {
        const valueA = getCashEventSortValue(a.event, cashEventSortConfig.key);
        const valueB = getCashEventSortValue(b.event, cashEventSortConfig.key);
        const result = compareValues(valueA, valueB, cashEventSortConfig.direction);

        if (result !== 0) {
          return result;
        }

        return a.index - b.index;
      })
      .map(({ event }) => event);
  }, [cashEvents, cashEventSortConfig]);

  const cashEventsTotal = useMemo(() => {
    let total = 0;
    let hasValue = false;

    cashEvents.forEach((event) => {
      const value = parseMaybeNumber(event.amount);

      if (typeof value === 'number' && Number.isFinite(value)) {
        total += value;
        hasValue = true;
      }
    });

    return hasValue ? total : null;
  }, [cashEvents]);

  const apiUrl = process.env.REACT_APP_API_URL;
  const username = process.env.REACT_APP_API_USERNAME ?? '';
  const password = process.env.REACT_APP_API_PASSWORD ?? '';

  const authHeader = useMemo(() => {
    if (!username || !password) {
      return null;
    }

    return `Basic ${btoa(`${username}:${password}`)}`;
  }, [username, password]);

  const commonHeaders = useMemo<Record<string, string> | null>(() => {
    if (!authHeader) {
      return null;
    }

    return {
      Authorization: authHeader,
      Accept: 'application/json',
    };
  }, [authHeader]);

  const handleCategoryHeaderClick = useCallback((key: CategorySortKey) => {
    setCategorySortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }

      return { key, direction: 'asc' };
    });
  }, []);

  const handleCashEventHeaderClick = useCallback((key: CashEventSortKey) => {
    setCashEventSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }

      return { key, direction: 'asc' };
    });
  }, []);

  const sortButtonClassName =
    'bg-transparent border-0 p-0 text-start text-decoration-none w-100 d-flex align-items-center gap-1';
  const sortButtonStyle: React.CSSProperties = {
    color: 'inherit',
    cursor: 'pointer',
    fontWeight: 'inherit',
  };

  const renderCategoryHeader = (
    label: string,
    key: CategorySortKey,
    options: { minWidth?: string } = {},
  ): JSX.Element => {
    const isActive = categorySortConfig.key === key;

    return (
      <th
        style={options.minWidth ? { minWidth: options.minWidth } : undefined}
        scope="col"
        aria-sort={getAriaSort(isActive, categorySortConfig.direction)}
      >
        <button
          type="button"
          className={sortButtonClassName}
          style={sortButtonStyle}
          onClick={() => handleCategoryHeaderClick(key)}
          title={`Sort by ${label}`}
        >
          <span>{label}</span>
          {isActive && (
            <span aria-hidden="true">
              {categorySortConfig.direction === 'asc' ? '▲' : '▼'}
            </span>
          )}
          <span className="visually-hidden">
            {isActive
              ? `Sorted ${categorySortConfig.direction === 'asc' ? 'ascending' : 'descending'}`
              : `Activate to sort by ${label}`}
          </span>
        </button>
      </th>
    );
  };

  const renderCashEventHeader = (
    label: string,
    key: CashEventSortKey,
    options: { minWidth?: string } = {},
  ): JSX.Element => {
    const isActive = cashEventSortConfig.key === key;

    return (
      <th
        style={options.minWidth ? { minWidth: options.minWidth } : undefined}
        scope="col"
        aria-sort={getAriaSort(isActive, cashEventSortConfig.direction)}
      >
        <button
          type="button"
          className={sortButtonClassName}
          style={sortButtonStyle}
          onClick={() => handleCashEventHeaderClick(key)}
          title={`Sort by ${label}`}
        >
          <span>{label}</span>
          {isActive && (
            <span aria-hidden="true">
              {cashEventSortConfig.direction === 'asc' ? '▲' : '▼'}
            </span>
          )}
          <span className="visually-hidden">
            {isActive
              ? `Sorted ${cashEventSortConfig.direction === 'asc' ? 'ascending' : 'descending'}`
              : `Activate to sort by ${label}`}
          </span>
        </button>
      </th>
    );
  };

  const fetchCategories = useCallback(async () => {
    if (!apiUrl) {
      setError('API URL is not configured.');
      setCategories([]);
      return;
    }

    if (!commonHeaders) {
      setError('API credentials are not configured.');
      setCategories([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<Category[]>(`${apiUrl}/categories`, {
        headers: commonHeaders,
      });
      setCategories(response.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load categories.'));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, commonHeaders]);

  const fetchFreeAgentCategories = useCallback(async () => {
    if (!apiUrl || !commonHeaders) {
      return;
    }

    setFreeAgentLoading(true);
    setFreeAgentError(null);

    try {
      const response = await axios.get<FreeAgentCategoryGroups>(`${apiUrl}/freeagent-categories`, {
        headers: commonHeaders,
        params: { includeSubAccounts: true },
      });
      setFreeAgentCategoryGroups(response.data);
    } catch (err) {
      setFreeAgentCategoryGroups({});
      setFreeAgentError(getErrorMessage(err, 'Failed to load FreeAgent categories.'));
    } finally {
      setFreeAgentLoading(false);
    }
  }, [apiUrl, commonHeaders]);

  const fetchCashEvents = useCallback(async () => {
    if (!apiUrl) {
      setCashEventsError('API URL is not configured.');
      setCashEvents([]);
      return;
    }

    if (!commonHeaders) {
      setCashEventsError('API credentials are not configured.');
      setCashEvents([]);
      return;
    }

    if (!cashEventsStartDate || !cashEventsEndDate) {
      setCashEventsError('Please select both a start date and an end date.');
      setCashEvents([]);
      return;
    }

    setCashEventsLoading(true);
    setCashEventsError(null);

    try {
      const response = await axios.get<CashEvent[]>(`${apiUrl}/cash-events`, {
        headers: commonHeaders,
        params: {
          startDate: toApiDateTime(cashEventsStartDate),
          endDate: toApiDateTime(cashEventsEndDate, true),
        },
      });

      setCashEvents(response.data);
    } catch (err) {
      setCashEventsError(getErrorMessage(err, 'Failed to load cash events.'));
      setCashEvents([]);
    } finally {
      setCashEventsLoading(false);
    }
  }, [apiUrl, cashEventsEndDate, cashEventsStartDate, commonHeaders]);

  useEffect(() => {
    void fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    void fetchFreeAgentCategories();
  }, [fetchFreeAgentCategories]);

  useEffect(() => {
    void fetchCashEvents();
  }, [fetchCashEvents]);

  const hasSelectedCategoryInOptions = useMemo(() => {
    if (!formData.categoryUrl) {
      return false;
    }

    return Object.values(freeAgentCategoryGroups).some((group) =>
      group.some((category) => category.url === formData.categoryUrl),
    );
  }, [formData.categoryUrl, freeAgentCategoryGroups]);

  const handleOpenCreateModal = () => {
    setFormData({ ...initialCategoryFormData });
    setFormError(null);
    setEditingCategoryId(null);
    setShowFormModal(true);

    if (!freeAgentLoading && Object.keys(freeAgentCategoryGroups).length === 0) {
      void fetchFreeAgentCategories();
    }
  };

  const handleOpenEditModal = (category: Category) => {
    setFormData({
      categoryName: category.categoryName ?? '',
      categoryUrl: category.categoryUrl,
      type: category.type,
      startDateTime: toDateInputValue(category.startDateTime),
      endDateTime: toDateInputValue(category.endDateTime),
      interval: formatIntervalInput(category.interval),
      amount: formatNumberInput(category.amount),
    });
    setFormError(null);
    setEditingCategoryId(category.id);
    setShowFormModal(true);

    if (!freeAgentLoading && Object.keys(freeAgentCategoryGroups).length === 0) {
      void fetchFreeAgentCategories();
    }
  };

  const handleCloseFormModal = () => {
    if (saving) {
      return;
    }

    setShowFormModal(false);
    setFormError(null);
    setFormData({ ...initialCategoryFormData });
    setEditingCategoryId(null);
  };

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!apiUrl) {
      setFormError('API URL is not configured.');
      return;
    }

    if (!commonHeaders) {
      setFormError('API credentials are not configured.');
      return;
    }

    if (!formData.categoryUrl) {
      setFormError('Please select or enter a FreeAgent category.');
      return;
    }

    setSaving(true);
    setFormError(null);

    const trimmedCategoryName = formData.categoryName.trim();
    const normalizedInterval = formData.interval.trim();
    const normalizedAmount = formData.amount.trim();

    const parsedIntervalDays = normalizedInterval ? Number.parseFloat(normalizedInterval) : null;
    const parsedAmount = normalizedAmount ? Number.parseFloat(normalizedAmount) : null;

    const intervalInSeconds =
      parsedIntervalDays === null || Number.isNaN(parsedIntervalDays)
        ? null
        : Math.round(parsedIntervalDays * SECONDS_IN_DAY);

    const payload: CategoryRequestPayload = {
      categoryUrl: formData.categoryUrl,
      type: formData.type,
      categoryName: trimmedCategoryName ? trimmedCategoryName : null,
      startDateTime: formData.startDateTime ? toApiDateTime(formData.startDateTime) : null,
      endDateTime: formData.endDateTime ? toApiDateTime(formData.endDateTime, true) : null,
      interval: intervalInSeconds,
      amount: Number.isNaN(parsedAmount) ? null : parsedAmount,
    };

    try {
      if (editingCategoryId !== null) {
        const response = await axios.put<Category>(
          `${apiUrl}/categories/${editingCategoryId}`,
          payload,
          {
            headers: { ...commonHeaders, 'Content-Type': 'application/json' },
          },
        );

        setCategories((prev) =>
          prev.map((category) => (category.id === editingCategoryId ? response.data : category)),
        );
      } else {
        const response = await axios.post<Category>(
          `${apiUrl}/categories`,
          payload,
          {
            headers: { ...commonHeaders, 'Content-Type': 'application/json' },
          },
        );

        setCategories((prev) => [...prev, response.data]);
      }

      setShowFormModal(false);
      setFormData({ ...initialCategoryFormData });
      setEditingCategoryId(null);
    } catch (err) {
      setFormError(getErrorMessage(err, 'Failed to save category.'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    if (!apiUrl) {
      setDeleteError('API URL is not configured.');
      return;
    }

    if (!commonHeaders) {
      setDeleteError('API credentials are not configured.');
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await axios.delete(`${apiUrl}/categories/${deleteTarget.id}`, {
        headers: commonHeaders,
      });

      setCategories((prev) => prev.filter((category) => category.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(getErrorMessage(err, 'Failed to delete category.'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3">
        <div>
          <h2 className="mb-0">Categories</h2>
          <p className="mb-0 text-muted">
            Manage Gemstone classifications that map to FreeAgent categories.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => {
              void fetchCategories();
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </Button>
          <Button onClick={handleOpenCreateModal}>Add Category</Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="table-responsive">
        <Table bordered hover responsive>
          <thead>
            <tr>
              {renderCategoryHeader('ID', 'id', { minWidth: '90px' })}
              {renderCategoryHeader('Category name', 'categoryName', { minWidth: '180px' })}
              {renderCategoryHeader('Type', 'type', { minWidth: '150px' })}
              {renderCategoryHeader('Start date', 'startDateTime', { minWidth: '180px' })}
              {renderCategoryHeader('End date', 'endDateTime', { minWidth: '180px' })}
              {renderCategoryHeader('Interval', 'interval', { minWidth: '120px' })}
              {renderCategoryHeader('Amount', 'amount', { minWidth: '140px' })}
              {renderCategoryHeader('Description', 'description')}
              <th scope="col" style={{ minWidth: '200px' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-center">
                  <Spinner animation="border" role="status" className="me-2" />
                  Loading categories...
                </td>
              </tr>
            )}

            {!loading && categories.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-muted">
                  {error ? 'Unable to display categories.' : 'No categories found yet.'}
                </td>
              </tr>
            )}

            {!loading &&
              sortedCategories.map((category) => (
                <tr key={category.id}>
                  <td>{category.id}</td>
                  <td>{category.categoryName ?? '—'}</td>
                  <td>
                    <Badge bg="info" text="dark">
                      {categoryTypeLabels[category.type] ?? category.type}
                    </Badge>
                  </td>
                  <td>{formatDateTime(category.startDateTime)}</td>
                  <td>{formatDateTime(category.endDateTime)}</td>
                  <td>{formatIntervalDisplay(category.interval)}</td>
                  <td>{formatAmount(category.amount)}</td>
                  <td>{category.freeagentCategory?.description ?? '—'}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setViewingCategory(category)}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleOpenEditModal(category)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setDeleteTarget(category);
                          setDeleteError(null);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>

      <section className="mt-5">
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3">
          <div>
            <h3 className="mb-0">Cash events</h3>
            <p className="mb-0 text-muted">
              Review projected cash movements calculated from scheduled categories.
            </p>
          </div>
        </div>

        <Form
          className="row g-3 align-items-end mb-3"
          onSubmit={(event) => {
            event.preventDefault();
            void fetchCashEvents();
          }}
        >
          <Form.Group className="col-sm-6 col-md-3" controlId="cashEventsStartDate">
            <Form.Label>Start date</Form.Label>
            <Form.Control
              type="date"
              value={cashEventsStartDate}
              onChange={(event) => setCashEventsStartDate(event.target.value)}
              disabled={cashEventsLoading}
              required
            />
          </Form.Group>
          <Form.Group className="col-sm-6 col-md-3" controlId="cashEventsEndDate">
            <Form.Label>End date</Form.Label>
            <Form.Control
              type="date"
              value={cashEventsEndDate}
              onChange={(event) => setCashEventsEndDate(event.target.value)}
              disabled={cashEventsLoading}
              required
            />
          </Form.Group>
          <div className="col-sm-12 col-md-auto">
            <Button
              type="submit"
              disabled={cashEventsLoading || !cashEventsStartDate || !cashEventsEndDate}
            >
              {cashEventsLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading events...
                </>
              ) : (
                'Load cash events'
              )}
            </Button>
          </div>
        </Form>

        {cashEventsError && <Alert variant="danger">{cashEventsError}</Alert>}

        <div className="table-responsive">
          <Table bordered hover responsive>
            <thead>
              <tr>
                {renderCashEventHeader('Category ID', 'categoryId', { minWidth: '90px' })}
                {renderCashEventHeader('Category', 'category', { minWidth: '200px' })}
                {renderCashEventHeader('Category type', 'categoryType', { minWidth: '150px' })}
                {renderCashEventHeader('Occurrence', 'occurrence', { minWidth: '200px' })}
                {renderCashEventHeader('Amount', 'amount', { minWidth: '140px' })}
                {renderCashEventHeader('Description', 'description')}
              </tr>
            </thead>
            <tbody>
                {cashEventsLoading && (
                  <tr>
                    <td colSpan={6} className="text-center">
                      <Spinner animation="border" role="status" className="me-2" />
                      Loading cash events...
                    </td>
                  </tr>
                )}

                {!cashEventsLoading && cashEvents.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      {cashEventsError
                        ? 'Unable to display cash events.'
                        : 'No cash events found for the selected period.'}
                    </td>
                  </tr>
              )}

              {!cashEventsLoading &&
                sortedCashEvents.map((cashEvent, index) => {
                  const eventDate = getCashEventDate(cashEvent);
                  const description = cashEvent.description ?? cashEvent.notes ?? '—';
                  const key =
                    cashEvent.id !== undefined
                      ? `cash-event-${cashEvent.id}`
                      : `cash-event-${cashEvent.categoryId ?? 'unknown'}-${cashEvent.occursAt ?? index}`;

                  return (
                    <tr key={key}>
                      <td>{cashEvent.categoryId ?? cashEvent.id ?? '—'}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <span>{cashEvent.categoryName ?? '—'}</span>
                          {cashEvent.categoryUrl && (
                            <a href={cashEvent.categoryUrl} target="_blank" rel="noreferrer">
                              {cashEvent.categoryUrl}
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        {cashEvent.categoryType ? (
                          <Badge bg="info" text="dark">
                            {formatCategoryType(cashEvent.categoryType)}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{formatDateTime(eventDate)}</td>
                      <td>{formatAmount(cashEvent.amount)}</td>
                      <td>{description}</td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </div>

        {!cashEventsLoading && cashEvents.length > 0 && (
          <div className="d-flex justify-content-end mt-3">
            <strong>
              Total: {cashEventsTotal !== null ? formatAmount(cashEventsTotal) : '—'}
            </strong>
          </div>
        )}
      </section>

      <Modal show={showFormModal} onHide={handleCloseFormModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingCategoryId !== null ? 'Edit Category' : 'Add Category'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleFormSubmit} id="category-form">
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}

            <Form.Group className="mb-3" controlId="categoryUrl">
              <Form.Label>FreeAgent category</Form.Label>
              {freeAgentLoading ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" role="status" />
                  <span>Loading FreeAgent categories...</span>
                </div>
              ) : Object.keys(freeAgentCategoryGroups).length > 0 ? (
                <Form.Select
                  name="categoryUrl"
                  value={formData.categoryUrl}
                  onChange={handleFormChange}
                  disabled={saving}
                >
                  <option value="">Select a FreeAgent category</option>
                  {!hasSelectedCategoryInOptions && formData.categoryUrl && (
                    <option value={formData.categoryUrl}>{formData.categoryUrl}</option>
                  )}
                  {Object.entries(freeAgentCategoryGroups).map(([groupName, groupCategories]) => (
                    <optgroup key={groupName} label={groupName}>
                      {groupCategories
                        .filter((groupCategory) => Boolean(groupCategory.url))
                        .map((groupCategory) => (
                          <option key={groupCategory.url} value={groupCategory.url}>
                            {formatFreeAgentCategoryLabel(groupCategory)}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  name="categoryUrl"
                  value={formData.categoryUrl}
                  onChange={handleFormChange}
                  placeholder="https://api.freeagent.com/v2/categories/001"
                  disabled={saving}
                />
              )}
              <Form.Text className="text-muted">
                Choose the FreeAgent category to link or paste the category URL if the list is unavailable.
              </Form.Text>
              {freeAgentError && (
                <Alert variant="warning" className="mt-2">
                  {freeAgentError}
                  <div className="mt-2">
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => {
                        void fetchFreeAgentCategories();
                      }}
                      disabled={freeAgentLoading}
                    >
                      Retry loading categories
                    </Button>
                  </div>
                </Alert>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="categoryName">
              <Form.Label>Category name</Form.Label>
              <Form.Control
                type="text"
                name="categoryName"
                value={formData.categoryName}
                onChange={handleFormChange}
                placeholder="Enter a display name"
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Optional label shown in the categories table; leave blank to use the FreeAgent description.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="type">
              <Form.Label>Gemstone classification</Form.Label>
              <Form.Select
                name="type"
                value={formData.type}
                onChange={handleFormChange}
                disabled={saving}
              >
                {Object.entries(categoryTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6" controlId="startDateTime">
                <Form.Label>Start date</Form.Label>
                <Form.Control
                  type="date"
                  name="startDateTime"
                  value={formData.startDateTime}
                  onChange={handleFormChange}
                  disabled={saving}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6" controlId="endDateTime">
                <Form.Label>End date</Form.Label>
                <Form.Control
                  type="date"
                  name="endDateTime"
                  value={formData.endDateTime}
                  onChange={handleFormChange}
                  disabled={saving}
                />
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className="mb-3 col-md-6" controlId="interval">
                <Form.Label>Interval (days)</Form.Label>
                <Form.Control
                  type="number"
                  name="interval"
                  value={formData.interval}
                  onChange={handleFormChange}
                  min="0"
                  step="0.01"
                  placeholder="e.g. 30"
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  Enter the recurrence interval in days. It will be converted to seconds when
                  saved.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3 col-md-6" controlId="amount">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleFormChange}
                  step="0.01"
                  placeholder="e.g. 1000"
                  disabled={saving}
                />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseFormModal} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                editingCategoryId !== null ? 'Update category' : 'Create category'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(viewingCategory)} onHide={() => setViewingCategory(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Category details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingCategory && (
            <>
              <Table bordered size="sm" responsive className="mb-4">
                <tbody>
                  <tr>
                    <th scope="row">ID</th>
                    <td>{viewingCategory.id}</td>
                  </tr>
                  <tr>
                    <th scope="row">Category name</th>
                    <td>{viewingCategory.categoryName ?? '—'}</td>
                  </tr>
                  <tr>
                    <th scope="row">Category URL</th>
                    <td>
                      {viewingCategory.categoryUrl ? (
                        <a href={viewingCategory.categoryUrl} target="_blank" rel="noreferrer">
                          {viewingCategory.categoryUrl}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Type</th>
                    <td>{categoryTypeLabels[viewingCategory.type] ?? viewingCategory.type}</td>
                  </tr>
                  <tr>
                    <th scope="row">Start date</th>
                    <td>{formatDateTime(viewingCategory.startDateTime)}</td>
                  </tr>
                  <tr>
                    <th scope="row">End date</th>
                    <td>{formatDateTime(viewingCategory.endDateTime)}</td>
                  </tr>
                  <tr>
                    <th scope="row">Interval</th>
                    <td>{formatIntervalDisplay(viewingCategory.interval)}</td>
                  </tr>
                  <tr>
                    <th scope="row">Amount</th>
                    <td>{formatAmount(viewingCategory.amount)}</td>
                  </tr>
                </tbody>
              </Table>

              <h5>FreeAgent details</h5>
              {viewingCategory.freeagentCategory ? (
                <Table bordered size="sm" responsive>
                  <tbody>
                    <tr>
                      <th scope="row">Nominal code</th>
                      <td>{viewingCategory.freeagentCategory.nominalCode ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Description</th>
                      <td>{viewingCategory.freeagentCategory.description ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Group</th>
                      <td>{viewingCategory.freeagentCategory.groupDescription ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Allowable for tax</th>
                      <td>
                        {viewingCategory.freeagentCategory.allowableForTax === undefined ||
                        viewingCategory.freeagentCategory.allowableForTax === null
                          ? '—'
                          : viewingCategory.freeagentCategory.allowableForTax
                          ? 'Yes'
                          : 'No'}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Tax reporting name</th>
                      <td>{viewingCategory.freeagentCategory.taxReportingName ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Automatic sales tax rate</th>
                      <td>{viewingCategory.freeagentCategory.autoSalesTaxRate ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Bank account</th>
                      <td>{viewingCategory.freeagentCategory.bankAccount ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Capital asset type</th>
                      <td>{viewingCategory.freeagentCategory.capitalAssetType ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Stock item</th>
                      <td>{viewingCategory.freeagentCategory.stockItem ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Hire purchase</th>
                      <td>{viewingCategory.freeagentCategory.hirePurchase ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">User</th>
                      <td>{viewingCategory.freeagentCategory.user ?? '—'}</td>
                    </tr>
                    <tr>
                      <th scope="row">Category group</th>
                      <td>{viewingCategory.freeagentCategory.categoryGroup ?? '—'}</td>
                    </tr>
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted mb-0">No FreeAgent data available for this category.</p>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && <Alert variant="danger">{deleteError}</Alert>}
          <p className="mb-0">
            Are you sure you want to delete{' '}
            <strong>{deleteTarget?.freeagentCategory?.description ?? deleteTarget?.categoryUrl}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CategoriesPage;
